/**
 * MuaqqibAssignPanel — Office picks one muaqqib AND the travelers he/she
 * serves. "Select all" is the default so the muaqqib covers every member
 * unless the office explicitly narrows the assignment. This eliminates
 * disputes about which family members the muaqqib was paid to handle.
 */
import { useEffect, useMemo, useState } from "react";
import { UserCheck, Loader2, X, Users, Check } from "lucide-react";
import { api, formatApiError } from "@/api/client";

export default function MuaqqibAssignPanel({ application, onChanged }) {
  const appId = application?.id || application?._id;
  const members = useMemo(() => application?.members || [], [application]);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [picker, setPicker] = useState(false);
  const [pickedMuaqqibId, setPickedMuaqqibId] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);

  const hasMuaqqib = !!application?.muaqqib_service || (Array.isArray(application?.pricing_items) && application.pricing_items.some((it) => {
    const code = (it.code || "").toLowerCase();
    const label = (it.label || it.name_ar || "").toLowerCase();
    return code.includes("muaqqib") || label.includes("معقب") || label.includes("معقّب");
  }));

  useEffect(() => {
    if (!picker) return;
    setLoading(true);
    setPickedMuaqqibId(null);
    // Default = ALL members selected (prevents disputes).
    setSelectedMembers((members || []).map((m) => m.id).filter(Boolean));
    api.get("/admin/muaqqibs")
      .then(({ data }) => setList((data.items || []).filter((m) => m.active)))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [picker, members]);

  if (!hasMuaqqib) return null;

  const submitAssignment = async () => {
    if (!pickedMuaqqibId) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/applications/${appId}/assign-muaqqib`, {
        muaqqib_id: pickedMuaqqibId,
        member_ids: selectedMembers,
      });
      setPicker(false);
      onChanged?.();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const unassign = async () => {
    if (!window.confirm("إلغاء إسناد المعقّب من هذه المعاملة؟")) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/admin/applications/${appId}/assign-muaqqib`);
      onChanged?.();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const current = application?.muaqqib_assigned_name;
  const assignedIds = application?.muaqqib_member_ids || [];
  const coversAll = !!application?.muaqqib_covers_all;

  const toggleMember = (mid) => {
    setSelectedMembers((prev) =>
      prev.includes(mid) ? prev.filter((x) => x !== mid) : [...prev, mid]
    );
  };
  const allSelected = members.length > 0 && selectedMembers.length === members.length;
  const toggleAll = () => {
    setSelectedMembers(allSelected ? [] : members.map((m) => m.id).filter(Boolean));
  };

  // Names of covered members for the "current" badge.
  const coveredNames = (() => {
    if (!current) return [];
    if (coversAll) return ["جميع المسافرين"];
    return members
      .filter((m) => assignedIds.includes(m.id))
      .map((m) => m.full_name || "—");
  })();

  return (
    <div style={css.card} data-testid="muaqqib-assign-panel">
      <div style={css.head}>
        <UserCheck size={18} strokeWidth={2.2} style={{ color: "#d4af37" }} />
        <h3 style={css.title}>المعقّب المسؤول عن المعاملة</h3>
      </div>
      {current ? (
        <div style={css.currentBox}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 14 }}>{current}</div>
            <div style={{ fontSize: 11, opacity: 0.8, marginTop: 4, lineHeight: 1.6 }}>
              {coversAll ? (
                <span style={{ color: "#16a34a", fontWeight: 800 }}>✓ يخدم جميع المسافرين</span>
              ) : (
                <>
                  <span style={{ fontWeight: 700, color: "#d4af37" }}>يخدم:</span>{" "}
                  {coveredNames.join("، ") || "—"}
                </>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setPicker(true)} style={css.btnSm} data-testid="muaqqib-change-btn">تغيير</button>
            <button onClick={unassign} style={{ ...css.btnSm, color: "#dc2626", borderColor: "#dc2626" }} data-testid="muaqqib-unassign-btn">إلغاء الإسناد</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setPicker(true)} style={css.btnGold} data-testid="muaqqib-pick-btn">
          <UserCheck size={14} strokeWidth={2.4} /> اختيار المعقّب
        </button>
      )}

      {picker && (
        <div style={css.modalBack} onClick={() => setPicker(false)}>
          <div style={css.modal} onClick={(e) => e.stopPropagation()}>
            <div style={css.modalHead}>
              <strong>إسناد المعقّب</strong>
              <button onClick={() => setPicker(false)} style={{ background: "transparent", border: "none", cursor: "pointer", opacity: 0.6 }}><X size={14} /></button>
            </div>

            <div style={{ padding: 12, maxHeight: "70vh", overflowY: "auto" }}>
              {/* Step 1 — pick the muaqqib */}
              <div style={css.stepLabel}>1) اختر المعقّب</div>
              {loading ? (
                <div style={{ textAlign: "center", padding: 16 }}><Loader2 className="vta-spin" /></div>
              ) : list.length === 0 ? (
                <div style={{ textAlign: "center", padding: 20, opacity: 0.7 }}>
                  لا يوجد معقّبون نشطون. أضف من <strong>لوحة التحكم → دليل المعقّبين</strong>.
                </div>
              ) : list.map((m) => (
                <button key={m.id} onClick={() => setPickedMuaqqibId(m.id)}
                  style={{
                    ...css.listItem,
                    background: pickedMuaqqibId === m.id ? "rgba(212,175,55,0.20)" : "rgba(212,175,55,0.05)",
                    borderColor: pickedMuaqqibId === m.id ? "#d4af37" : "rgba(212,175,55,0.2)",
                  }}
                  data-testid={`muaqqib-option-${m.id}`}>
                  <div style={{ textAlign: "start", flex: 1 }}>
                    <div style={{ fontWeight: 800 }}>{m.full_name}</div>
                    <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }} dir="ltr">
                      ID: {m.national_id} • 📞 {m.phone}
                    </div>
                  </div>
                  {pickedMuaqqibId === m.id && <Check size={16} strokeWidth={2.8} style={{ color: "#16a34a" }} />}
                  {!m.id_image_present && <span style={{ fontSize: 10, color: "#f59e0b" }}>⚠ بلا هوية</span>}
                </button>
              ))}

              {/* Step 2 — pick travelers (only enabled after muaqqib picked) */}
              {pickedMuaqqibId && (
                <>
                  <div style={{ ...css.stepLabel, marginTop: 16 }}>
                    <Users size={14} strokeWidth={2.4} style={{ marginInlineEnd: 6, verticalAlign: "-2px" }} />
                    2) المسافرون الذين يخدمهم المعقّب
                  </div>
                  <button onClick={toggleAll} style={css.selectAllBtn} data-testid="muaqqib-toggle-all">
                    <input type="checkbox" checked={allSelected} readOnly
                      style={{ accentColor: "#d4af37", width: 16, height: 16 }} />
                    <span style={{ fontWeight: 900 }}>
                      {allSelected ? "إلغاء تحديد الجميع" : "تحديد الجميع"}
                    </span>
                    <span style={{ marginInlineStart: "auto", fontSize: 11, opacity: 0.75 }}>
                      {selectedMembers.length} / {members.length}
                    </span>
                  </button>
                  {members.length === 0 && (
                    <div style={{ fontSize: 12, opacity: 0.7, padding: 10 }}>
                      لا يوجد مسافرون مسجّلون لهذه المعاملة.
                    </div>
                  )}
                  {members.map((m) => {
                    const checked = selectedMembers.includes(m.id);
                    return (
                      <label key={m.id} style={css.memberRow}
                        data-testid={`muaqqib-member-${m.id}`}>
                        <input type="checkbox" checked={checked}
                          onChange={() => toggleMember(m.id)}
                          style={{ accentColor: "#d4af37", width: 16, height: 16 }}
                          data-testid={`muaqqib-member-checkbox-${m.id}`}
                        />
                        <div style={{ flex: 1, textAlign: "start" }}>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{m.full_name || "—"}</div>
                          <div style={{ fontSize: 10, opacity: 0.65, marginTop: 2 }}>
                            {m.is_companion
                              ? `عمالة منزلية${m.job_title ? ` — ${m.job_title}` : ""}`
                              : (m.relationship_to_main === "self" ? "صاحب الطلب" : m.relationship_to_main || "مسافر")}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </>
              )}
              {err && <div style={css.err}>{err}</div>}
            </div>

            <div style={css.modalFoot}>
              <button onClick={() => setPicker(false)} style={css.btnSm} disabled={busy}>
                إلغاء
              </button>
              <button onClick={submitAssignment}
                disabled={!pickedMuaqqibId || selectedMembers.length === 0 || busy}
                style={{
                  ...css.btnGold,
                  opacity: (!pickedMuaqqibId || selectedMembers.length === 0 || busy) ? 0.5 : 1,
                }}
                data-testid="muaqqib-confirm-btn">
                {busy ? <Loader2 size={14} className="vta-spin" /> : <Check size={14} strokeWidth={2.6} />}
                حفظ الإسناد
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const css = {
  card: { background: "rgba(212,175,55,0.04)", border: "1.5px solid rgba(212,175,55,0.25)", borderRadius: 14, padding: 16, marginTop: 16 },
  head: { display: "flex", alignItems: "center", gap: 10, marginBottom: 10 },
  title: { margin: 0, fontSize: 14, fontWeight: 900, color: "#d4af37" },
  currentBox: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: 12, background: "rgba(22,163,74,0.08)", border: "1px solid #16a34a", borderRadius: 10, gap: 12, flexWrap: "wrap" },
  btnGold: { background: "#d4af37", color: "#0a0a0a", border: "none", padding: "9px 16px", borderRadius: 10, fontWeight: 900, fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 },
  btnSm: { background: "transparent", color: "inherit", border: "1px solid rgba(0,0,0,0.2)", padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" },
  modalBack: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 9999 },
  modal: { width: "100%", maxWidth: 520, background: "var(--surface,#1a1a1a)", color: "inherit", borderRadius: 14, overflow: "hidden", border: "1px solid rgba(212,175,55,0.3)", display: "flex", flexDirection: "column" },
  modalHead: { padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center" },
  modalFoot: { padding: 12, borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "flex-end", gap: 8 },
  stepLabel: { fontSize: 12, fontWeight: 900, color: "#d4af37", marginBottom: 8, letterSpacing: 0.2 },
  listItem: { width: "100%", textAlign: "start", padding: 10, marginBottom: 6, border: "1px solid", borderRadius: 8, cursor: "pointer", color: "inherit", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 },
  selectAllBtn: { width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", marginBottom: 8, background: "rgba(212,175,55,0.10)", border: "1.5px solid rgba(212,175,55,0.35)", borderRadius: 8, cursor: "pointer", color: "inherit", fontSize: 13 },
  memberRow: { display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", marginBottom: 4, background: "rgba(0,0,0,0.02)", border: "1px solid rgba(0,0,0,0.06)", borderRadius: 8, cursor: "pointer" },
  err: { background: "rgba(220,38,38,0.10)", color: "#dc2626", padding: 8, borderRadius: 8, fontSize: 11, marginTop: 8 },
};
